import { useMemo, useState, ChangeEvent } from 'react';
import { getIn, useField, useFormikContext } from 'formik';
import { Alert, Button, Divider, Typography } from '@mui/material';

import { InputField } from 'components';

import { FormOutcomesFieldProps } from './types';

const getNestedErrorMessage = (error: unknown): string | undefined => {
  if (typeof error === 'string') {
    return error;
  }

  if (!error || typeof error !== 'object') {
    return undefined;
  }

  if (Array.isArray(error)) {
    for (const item of error) {
      const message = getNestedErrorMessage(item);
      if (message) {
        return message;
      }
    }

    return undefined;
  }

  // Object.values follows V8 insertion order — stable in practice but not spec-guaranteed;
  // the Array.isArray branch above handles the primary Yup array-error case deterministically.
  for (const item of Object.values(error)) {
    const message = getNestedErrorMessage(item);
    if (message) {
      return message;
    }
  }

  return undefined;
};

export const FormOutcomesField = ({ name, value }: FormOutcomesFieldProps) => {
  const [, , { setValue }] = useField(name);
  const { errors, touched, submitCount } = useFormikContext();
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const optionsPath = `${name}.options`;
  const optionsTouched = getIn(touched, optionsPath);
  const conditionsError = getIn(errors, `${name}.options.conditions`);
  const outcomesError = getIn(errors, `${name}.options.outcomes`);
  const error =
    submitCount > 0 || Boolean(optionsTouched)
      ? (getNestedErrorMessage(conditionsError) ??
        getNestedErrorMessage(outcomesError))
      : undefined;

  const currentStats = useMemo(
    () => ({
      outcomes: value.options?.outcomes?.length ?? 0,
      conditions: value.options?.conditions?.length ?? 0,
    }),
    [value.options?.conditions?.length, value.options?.outcomes?.length]
  );

  const handleCsvImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      return;
    }

    try {
      const csvContent = await file.text();

      setValue(
        {
          ...value,
          options: {
            ...value.options,
            csvContent,
          },
        },
        true
      );

      setSelectedFileName(file.name);
    } catch {
      // file read failed — no-op, user can retry
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Divider />

      <div className="flex flex-col gap-2">
        <InputField
          fullWidth
          label="Condition Value"
          placeholder="Condition Value"
          name={`${name}.options.conditionValue`}
        />

        {(currentStats.outcomes > 0 || currentStats.conditions > 0) && (
          <Typography variant="body2">
            Stored outcomes: {currentStats.outcomes} outcomes,{' '}
            {currentStats.conditions} conditions.
          </Typography>
        )}

        {selectedFileName && (
          <Typography variant="body2">
            Selected CSV: {selectedFileName}
          </Typography>
        )}

        <div className="flex flex-wrap gap-2">
          <Button component="label" variant="outlined">
            Upload outcomes CSV
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={handleCsvImport}
            />
          </Button>
          <Button
            color="error"
            variant="outlined"
            disabled={!value.options?.csvContent}
            onClick={() => {
              setValue(
                {
                  ...value,
                  options: {
                    ...value.options,
                    csvContent: undefined,
                  },
                },
                true
              );
              setSelectedFileName(null);
            }}
          >
            Clear selected CSV
          </Button>
        </div>

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </div>

      <Alert severity="info">
        Options and conditions are read-only after processing.
      </Alert>
    </div>
  );
};
